import React, { useMemo } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Alert, Badge, Button, Col, Form } from 'react-bootstrap';
import DataTable from './components/DataTable';
import Loading from './components/Loading';
import useAdminData from './useAdminData';
import { formatMoney } from './format';

export const REVIEW_FILTERS = {
  all: { label: 'All items', matches: () => true },
  unreviewed: { label: 'Not reviewed', matches: item => !item.isReviewed },
  flagged: { label: 'Flagged', matches: item => item.isFlagged }
};

const COLUMNS = [
  { key: 'id', label: 'Item #', sortable: true },
  { key: 'title', label: 'Title', sortable: true },
  { key: 'category', label: 'Category', sortable: true, value: item => [item.category, item.subCategory].filter(Boolean).join(' / ') },
  { key: 'price', label: 'Price', sortable: true, value: item => Number(item.price), render: item => formatMoney(item.price) },
  { key: 'inventory', label: 'Qty', sortable: true },
  { key: 'consignerName', label: 'Consigner', sortable: true },
  {
    key: 'review',
    label: 'Review',
    value: item => [item.isFlagged && 'flagged', item.isReviewed && 'reviewed'].filter(Boolean).join(' '),
    render: item => (
      <>
        {item.isFlagged && <Badge variant="danger" className="mr-1">Flagged</Badge>}
        {item.isReviewed && <Badge variant="success">Reviewed</Badge>}
      </>
    )
  }
];

/** Items with their consigner's name, for searching and sorting */
export function withConsignerNames(items, consigners) {
  const names = new Map((consigners || []).map(c => [c.id, `${c.lastName}, ${c.firstName}`]));
  return (items || []).map(item => ({ ...item, consignerName: names.get(item.consignerId) || `#${item.consignerId}` }));
}

export default function ItemList() {
  const history = useHistory();
  const location = useLocation();
  const items = useAdminData('/items');
  const consigners = useAdminData('/admin/consigners');

  const params = new URLSearchParams(location.search);
  const consignerId = Number(params.get('consigner')) || null;
  const review = REVIEW_FILTERS[params.get('review')] ? params.get('review') : 'all';

  const rows = useMemo(
    () => withConsignerNames(items.data, consigners.data)
      .filter(item => !consignerId || item.consignerId === consignerId)
      .filter(REVIEW_FILTERS[review].matches),
    [items.data, consigners.data, consignerId, review]
  );

  const setFilter = (name, value) => {
    const next = new URLSearchParams(location.search);
    if (value) {
      next.set(name, value);
    } else {
      next.delete(name);
    }
    history.replace({ search: next.toString() });
  };

  const consigner = consignerId && (consigners.data || []).find(c => c.id === consignerId);
  const loading = items.loading || consigners.loading;
  const error = items.error || consigners.error;
  const reload = () => {
    items.reload();
    consigners.reload();
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <h2 className="mb-0">Items</h2>
        {items.data && <span className="ml-3 text-muted">{rows.length} shown</span>}
        <Button variant="outline-secondary" size="sm" className="ml-auto" onClick={reload} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && !items.data && <Loading waking={items.waking || consigners.waking} />}

      {consignerId && (
        <Alert variant="info" className="d-flex align-items-center py-2">
          <span>
            Showing items for{' '}
            {consigner
              ? <Link to={`/admin/consigners/${consignerId}`}>{consigner.firstName} {consigner.lastName}</Link>
              : `consigner #${consignerId}`}
          </span>
          <Button variant="link" size="sm" className="ml-auto" onClick={() => setFilter('consigner', null)}>Show all consigners</Button>
        </Alert>
      )}

      {items.data && (
        <>
          <Form.Row className="mb-2">
            <Col sm={4} md={3}>
              <Form.Control
                as="select"
                aria-label="Review filter"
                value={review}
                onChange={event => setFilter('review', event.target.value === 'all' ? null : event.target.value)}
              >
                {Object.entries(REVIEW_FILTERS).map(([value, filter]) => <option key={value} value={value}>{filter.label}</option>)}
              </Form.Control>
            </Col>
          </Form.Row>
          <DataTable
            columns={COLUMNS}
            rows={rows}
            searchable
            searchPlaceholder="Search by item number, title, category, or consigner"
            onRowClick={item => history.push(`/admin/items/${item.id}`)}
            emptyMessage="No items match"
          />
        </>
      )}
    </div>
  );
}
